import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import companyService, {
  type AddDocumentsRequest,
  type RegisterCompanyRequest,
  type RegisterCompanyResponse,
  type AddLegitimateUserRequest,
  type AddLegitimateUserToCompanyResponse,
  GetCompanyInfoRequest,
  type GetCompanyInfoResponse,
  type RefreshTokenRequest,
  type RefreshTokenResponse,
  type AddUserRequest,
  type UpdateCompanyUserRequest,
  type DeleteCompanyUserRequest,
  type LogoutRequest,
  type LogoutResponse,
  type ChangeUserStatusRequest,
  type UpdateCompanyRequest,
  type AddRequisitesRequest,
  type UpdateRequisitesRequest,
  type ChangeCompanyUserPasswordRequest,
  type MyAccessResponse,
  type ApprovalHistoryItem,
} from "../services/companyService";

// get-by-voen: bütün mərhələlərdə doldurulmuş məlumatlar (yalnız enabled olanda sorğu atır)
export function useGetCompanyByVoen(voen: string, enabled: boolean) {
  return useQuery({
    queryKey: ["company", "get-by-voen", voen],
    queryFn: async () => {
      const res = await companyService.getCompanyByVoen(voen);
      return res.responseValue;
    },
    enabled: enabled && !!voen,
    staleTime: 60_000,
  });
}

export function useRegisterCompany() {
  return useMutation({
    mutationKey: ["company", "register"],
    mutationFn: async (data: RegisterCompanyRequest) => {
      const res = await companyService.registerCompany(data);
      return res.responseValue as RegisterCompanyResponse;
    },
    onSuccess: (data) => {},
    onError: (error) => {
      console.error("Error registering company:", error);
    },
  });
}

export function useAddDocumentsToCompany() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["company", "add-documents"],
    mutationFn: async (payload: AddDocumentsRequest) => {
      const res = await companyService.addDocumentsToCompany(payload);
      return res.responseValue;
    },
    onSuccess: () => {
      // Refetch company info to get updated documents
      queryClient.invalidateQueries({ queryKey: ["company", "info"] });
    },
    onError: (error) => {
      console.error("Error adding documents:", error);
    },
  });
}

export function useAddLegitimateUserToCompany() {
  const queryClient = useQueryClient();

  return useMutation<
    AddLegitimateUserToCompanyResponse,
    Error,
    AddLegitimateUserRequest
  >({
    mutationKey: ["company", "add-legitimate-user"],
    mutationFn: async (payload: AddLegitimateUserRequest) => {
      const res = await companyService.addLegitimateUserToCompany(payload);
      return res.responseValue;
    },
    onSuccess: () => {
      // Refetch company info to get updated user list
      queryClient.invalidateQueries({ queryKey: ["company", "info"] });
    },
    onError: (error) => {
      console.error("Error adding legitimate user:", error);
    },
  });
}

export function useGetCompanyInfo(companyId: number, enabled = true) {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ["company", "info", companyId],
    queryFn: async () => {
      const res = await companyService.getCompanyInfo({ Id: companyId });
      return res.responseValue as GetCompanyInfoResponse;
    },
    enabled: enabled && !!companyId,
  });

  const refetchCompanyInfo = () => {
    queryClient.invalidateQueries({ queryKey: ["company", "info", companyId] });
  };

  return {
    ...query,
    refetchCompanyInfo,
  };
}

export function useRefreshToken() {
  return useMutation({
    mutationKey: ["company", "refresh-token"],
    mutationFn: async (data: RefreshTokenRequest) => {
      const res = await companyService.refreshToken(data);
      return res.responseValue as RefreshTokenResponse;
    },
    onSuccess: (data) => {},
    onError: (error) => {
      console.error("Error refreshing token:", error);
    },
  });
}

export function useAddUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["company", "add-user"],
    mutationFn: async (data: AddUserRequest) => {
      const res = await companyService.addUser(data);
      return res.responseValue;
    },
    onSuccess: () => {
      // Refetch company info to get updated user list
      queryClient.invalidateQueries({ queryKey: ["company", "info"] });
    },
    onError: (error) => {
      console.error("Error adding user:", error);
    },
  });
}

export function useUpdateCompanyUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["company", "update-user"],
    mutationFn: async (data: UpdateCompanyUserRequest) => {
      const res = await companyService.updateCompanyUser(data);
      return res.responseValue;
    },
    onSuccess: () => {
      // Refetch company info to get updated user data
      queryClient.invalidateQueries({ queryKey: ["company", "info"] });
    },
    onError: (error) => {
      console.error("Error updating user:", error);
    },
  });
}

export function useDeleteCompanyUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["company", "delete-user"],
    mutationFn: async (data: DeleteCompanyUserRequest) => {
      const res = await companyService.deleteCompanyUser(data);
      return res.responseValue;
    },
    onSuccess: () => {
      // Refetch company info to get updated user list
      queryClient.invalidateQueries({ queryKey: ["company", "info"] });
    },
    onError: (error) => {
      console.error("Error deleting user:", error);
    },
  });
}

export function useLogout() {
  return useMutation({
    mutationKey: ["company", "logout"],
    mutationFn: async (data: LogoutRequest) => {
      const res = await companyService.logout(data);
      return res.responseValue as LogoutResponse;
    },
    onSuccess: () => {
      // Logout successful - user will be redirected
    },
    onError: (error) => {
      console.error("Logout error:", error);
    },
  });
}

export function useChangeUserStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["company", "change-user-status"],
    mutationFn: async (data: ChangeUserStatusRequest) => {
      const res = await companyService.changeUserStatus(data);
      return res.responseValue;
    },
    onSuccess: () => {
      // Refetch company info to get updated user status
      queryClient.invalidateQueries({ queryKey: ["company", "info"] });
    },
    onError: (error) => {
      console.error("Error changing user status:", error);
    },
  });
}

export function useUpdateCompany() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["company", "update-company"],
    mutationFn: async (data: UpdateCompanyRequest) => {
      const res = await companyService.updateCompany(data);
      return res.responseValue;
    },
    onSuccess: () => {
      // Refetch company info to get updated data
      queryClient.invalidateQueries({ queryKey: ["company", "info"] });
    },
    onError: (error) => {
      console.error("Error updating company:", error);
    },
  });
}

export function useAddRequisites() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["company", "add-requisites"],
    mutationFn: async (data: AddRequisitesRequest) => {
      const res = await companyService.addRequisites(data);
      return res.responseValue;
    },
    onSuccess: () => {
      // Refetch company info to get updated requisites
      queryClient.invalidateQueries({ queryKey: ["company", "info"] });
    },
    onError: (error) => {
      console.error("Error adding requisites:", error);
    },
  });
}

export function useUpdateRequisites() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["company", "update-requisites"],
    mutationFn: async (data: UpdateRequisitesRequest) => {
      const res = await companyService.updateRequisites(data);
      return res.responseValue;
    },
    onSuccess: () => {
      // Refetch company info to get updated requisites
      queryClient.invalidateQueries({ queryKey: ["company", "info"] });
    },
    onError: (error) => {
      console.error("Error updating requisites:", error);
    },
  });
}

// Doc 4.1: cari istifadəçinin rolu/şirkət statusu/açıq bölmələr
export function useMyAccess(enabled = true) {
  return useQuery({
    queryKey: ["company", "my-access"],
    queryFn: async () => {
      const res = await companyService.getMyAccess();
      return res.responseValue as MyAccessResponse;
    },
    enabled,
    staleTime: 60_000,
    retry: false,
  });
}

// Doc 4.2: şirkəti hüquqşünas yoxlamasına göndər
export function useSubmitForApproval() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["company", "approval-submit"],
    mutationFn: async () => {
      const res = await companyService.submitForApproval();
      return res;
    },
    onSuccess: () => {
      // Status və açıq bölmələr dəyişir — yenidən oxu
      queryClient.invalidateQueries({ queryKey: ["company", "info"] });
      queryClient.invalidateQueries({ queryKey: ["company", "my-access"] });
    },
    onError: (error) => {
      console.error("Error submitting for approval:", error);
    },
  });
}

// Doc 4.3: təsdiq tarixçəsi
export function useApprovalHistory(enabled = true) {
  return useQuery({
    queryKey: ["company", "approval-history"],
    queryFn: async () => {
      const res = await companyService.getApprovalHistory();
      return res.responseValue as ApprovalHistoryItem[];
    },
    enabled,
    staleTime: 60_000,
    retry: false,
  });
}

export function useSendChangePasswordOtp() {
  return useMutation({
    mutationKey: ["company", "change-password", "send-otp"],
    mutationFn: async () => {
      const res = await companyService.sendChangePasswordOtp();
      return res.responseValue;
    },
  });
}

export function useChangeCompanyUserPassword() {
  return useMutation({
    mutationKey: ["company", "change-password"],
    mutationFn: async (data: ChangeCompanyUserPasswordRequest) => {
      const res = await companyService.changeCompanyUserPassword(data);
      return res;
    },
  });
}
